import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Linkedin, MapPin, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Footer = () => {
  const { t, isRTL } = useLanguage();
  const quickLinks = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/uber-uns' },
    { name: t('nav.triax'), href: '/triax' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.contact'), href: '/kontakt' }
  ];

  const services = [
    { name: 'Change Management', href: '/services#service-0' },
    { name: 'Interkulturelle Beratung', href: '/services#service-1' },
    { name: 'Scrum4Sales', href: '/services#service-2' },
    { name: 'Leadership Coaching', href: '/services#service-3' }
  ];

  const solutions = [
    { name: 'CultureBridge MENA', href: '/signature#signature-0' },
    { name: 'Scrum4Sales Sprint', href: '/signature#signature-1' },
    { name: 'Change Sparring 6', href: '/signature#signature-2' },
    { name: 'Leadership Atelier', href: '/signature#signature-3' },
    { name: 'SOS-Pakete', href: '/sos' }
  ];

  const regions = [
    { name: 'DACH Region' },
    { name: 'MENA Region' },
    { name: 'North Africa' }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <div className="text-2xl font-light">
                <span className="gradient-text font-medium">GLOB</span>
                <span className="text-white">RIDGE</span>
              </div>
            </Link>
            
            <p className="text-slate-300 leading-relaxed mb-6 font-light">
              {t('footer.claim')}
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center text-slate-300">
                <Mail className="w-4 h-4 mr-3 text-purple-400" />
                <span className="text-sm">contact@Globalegroupe.com</span>
              </div>
              <div className="flex items-center text-slate-300">
                <Linkedin className="w-4 h-4 mr-3 text-purple-400" />
                <span className="text-sm">linkedin.com/company/Globalegroupe</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-medium text-white mb-6">Navigation</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-300 hover:text-purple-400 transition-colors duration-200 text-sm flex items-center group"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services & Solutions */}
          <div>
            <h3 className="text-lg font-medium text-white mb-6">Lösungen</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-purple-400 mb-2">Services</h4>
                <ul className="space-y-2">
                  {services.map((service) => (
                    <li key={service.name}>
                      <Link
                        to={service.href}
                        className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                      >
                        {service.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-orange-400 mb-2">Lösungen</h4>
                <ul className="space-y-2">
                  {solutions.map((solution) => (
                    <li key={solution.name}>
                      <Link
                        to={solution.href}
                        className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                      >
                        {solution.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Global Presence */}
          <div>
            <h3 className="text-lg font-medium text-white mb-6">Global Präsenz</h3>
            <div className="space-y-4">
              {regions.map((region, index) => (
                <div key={index} className="flex items-start">
                  <MapPin className="w-4 h-4 mr-3 mt-1 text-purple-400 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-white">{region.name}</div>
                    <div className="text-xs text-slate-400">{region.location}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/sos"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-medium text-sm transition-all duration-300 hover:shadow-glow hover:scale-105"
              >
                <span className="mr-2">SOS Hotline</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-6 text-sm text-slate-400">
              <Link to="/datenschutz" className="hover:text-purple-400 transition-colors duration-200">
                Datenschutz
              </Link>
              <Link to="/impressum" className="hover:text-purple-400 transition-colors duration-200">
                Impressum
              </Link>
              <Link to="/agb" className="hover:text-purple-400 transition-colors duration-200">
                AGB
              </Link>
            </div>
            
            <div className="text-sm text-slate-400">
              © 2024 GLOBRIDGE. Alle Rechte vorbehalten.
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-slate-400">
                <Globe className="w-4 h-4 mr-1" />
                <span>DE | EN | AR | FR</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};